/**
 * Client-side image resize before upload.
 *
 * - Caps either dimension to `maxDimension` (default 2000 px)
 * - Returns JPEG for opaque images, PNG for images with transparency
 * - Passes GIFs through unchanged (to preserve animation)
 * - Returns original blob if already within bounds
 */
export async function resizeImageBlob(
  blob: Blob,
  maxDimension = 2000,
  quality = 0.85
): Promise<Blob> {
  // GIFs: return as-is to preserve animation frames
  if (blob.type === 'image/gif') return blob;

  const bitmap = await createImageBitmap(blob);
  const { width, height } = bitmap;

  // Already within bounds — return original
  if (width <= maxDimension && height <= maxDimension) {
    bitmap.close();
    return blob;
  }

  const scale = maxDimension / Math.max(width, height);
  const targetW = Math.round(width * scale);
  const targetH = Math.round(height * scale);

  const canvas = new OffscreenCanvas(targetW, targetH);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    return blob;
  }

  ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  bitmap.close();

  // Preserve PNG for images that might have transparency
  const hasAlpha = blob.type === 'image/png' || blob.type === 'image/webp';
  const outputType = hasAlpha ? 'image/png' : 'image/jpeg';

  const resized = await canvas.convertToBlob({
    type: outputType,
    quality: outputType === 'image/jpeg' ? quality : undefined,
  });

  return resized;
}
